import { useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import imgProxyLoader from 'helpers/imgProxyLoader';
import { CANONICAL_WIDTHS, snapWidth } from 'helpers/imageWidths';

function getCappedWidths(maxSrcSetWidth) {
  const snapped = parseInt(snapWidth(maxSrcSetWidth), 10);
  const max = Number.isFinite(snapped)
    ? snapped
    : CANONICAL_WIDTHS[CANONICAL_WIDTHS.length - 1];
  const widths = CANONICAL_WIDTHS.filter(width => width <= max);

  return widths.length ? widths : [CANONICAL_WIDTHS[0]];
}

function CappedProxyImage({
  src,
  alt,
  fill,
  sizes,
  maxSrcSetWidth,
  className,
  style,
  priority,
  loading,
  decoding = 'async',
  fetchPriority,
  width,
  height,
  quality,
  cheapPlaceholder,
  onError,
  ...rest
}) {
  // next/image has no per-instance max candidate width and deletes user
  // srcSet, so card/thumb images that need a hard ceiling render the same
  // proxy URLs directly.
  const widths = getCappedWidths(maxSrcSetWidth);
  const srcSet = widths
    .map(
      candidateWidth =>
        `${imgProxyLoader({
          src,
          width: candidateWidth,
          quality,
        })} ${candidateWidth}w`,
    )
    .join(', ');
  const srcAttr = imgProxyLoader({
    src,
    width: widths[widths.length - 1],
    quality,
  });
  const placeholderStyle =
    cheapPlaceholder &&
    cheapPlaceholder !== 'empty' &&
    cheapPlaceholder !== 'blur'
      ? {
          backgroundImage: `url(${JSON.stringify(cheapPlaceholder)})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }
      : undefined;
  const fillStyle = fill
    ? {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        color: 'transparent',
      }
    : undefined;
  const image = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      {...rest}
      alt={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      decoding={decoding}
      loading={priority ? 'eager' : loading || 'lazy'}
      fetchpriority={priority ? 'high' : fetchPriority}
      sizes={sizes}
      srcSet={srcSet}
      src={srcAttr}
      className={className}
      style={{
        ...placeholderStyle,
        ...style,
        ...fillStyle,
      }}
      onError={onError}
    />
  );

  if (!priority) return image;

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href={srcAttr}
          imageSrcSet={srcSet}
          imageSizes={sizes}
        />
      </Head>
      {image}
    </>
  );
}

// Compact placeholder for images whose origin has deleted the file —
// a quiet "postcard lost in transit" instead of the browser's broken-image
// glyph. Sized from the intended dimensions but capped so it never
// dominates the page.
function LostPhoto({ width, height, fill, alt }) {
  const w = Number(width) || undefined;
  const h = Number(height) || undefined;
  const tiny = !fill && w && w < 140;

  let style;
  if (!fill) {
    style = {
      aspectRatio: w && h ? `${w} / ${h}` : '3 / 2',
      width: '100%',
      maxWidth: w ? `${Math.min(w, 560)}px` : '560px',
      maxHeight: '15rem',
    };
  }

  return (
    <span
      role="img"
      aria-label={alt || 'Image no longer available'}
      className={`tw-mx-auto tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-overflow-hidden tw-rounded-xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-bg-[#F6F3E9] dark:tw-bg-[#16241F] tw-text-[#A8B0A3] dark:tw-text-[#4E635A] ${
        fill ? 'tw-absolute tw-inset-0' : ''
      }`}
      style={style}
    >
      <svg
        viewBox="0 0 48 36"
        className={tiny ? 'tw-h-5 tw-w-7' : 'tw-h-9 tw-w-12'}
        fill="none"
        aria-hidden="true"
      >
        <rect
          x="1.5"
          y="1.5"
          width="45"
          height="33"
          rx="3"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeDasharray="4 3"
        />
        <circle
          cx="33.5"
          cy="11.5"
          r="3.5"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <path
          d="M7 27.5l8.5-9 7 7.5 5.5-5 3.5 3.2"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {!tiny && (
        <span className="tw-px-3 tw-text-center tw-font-mono tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-[0.22em]">
          Photo lost in transit
        </span>
      )}
    </span>
  );
}

export default function ProxyFutureImage(props) {
  const [failed, setFailed] = useState(false);
  const { onError, placeholder, blurDataURL, maxSrcSetWidth, ...rest } = props;

  // A missing src or dimensions crashes next/image ("invalid width") and takes
  // the whole post down — e.g. a malformed gallery entry in a user's post.
  // Render nothing instead so one bad image can't white-screen the page.
  // Static imports carry their dimensions inside the src object — treat
  // them as sized, or this guard silently drops every static asset.
  const isStaticImport =
    typeof props.src === 'object' &&
    props.src !== null &&
    Number(props.src.width) > 0;
  const hasDimensions =
    props.fill ||
    maxSrcSetWidth ||
    isStaticImport ||
    (Number(props.width) > 0 && Number(props.height) > 0);
  if (!props.src || props.src === 'undefined' || !hasDimensions) return null;

  if (failed)
    return (
      <LostPhoto
        width={props.width}
        height={props.height}
        fill={props.fill}
        alt={props.alt}
      />
    );

  // placeholder="blur" makes next/image emit an inline SVG with two
  // feGaussianBlur passes per image; iOS Safari rasterizes those on the
  // CPU at device pixels, and image-heavy posts (100+ photos) crash the
  // tab ("a problem repeatedly occurred"). Passing the preview data URL
  // as the placeholder itself skips the SVG filter — the GPU's bilinear
  // upscale of the tiny preview looks near-identical for free.
  const cheapPlaceholder =
    placeholder === 'blur' &&
    blurDataURL &&
    blurDataURL.startsWith('data:image/')
      ? blurDataURL
      : placeholder;

  if (maxSrcSetWidth)
    return (
      <CappedProxyImage
        {...rest}
        maxSrcSetWidth={maxSrcSetWidth}
        cheapPlaceholder={cheapPlaceholder}
        onError={event => {
          setFailed(true);
          if (onError) onError(event);
        }}
      />
    );

  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <Image
      loader={imgProxyLoader}
      placeholder={cheapPlaceholder}
      blurDataURL={cheapPlaceholder === 'blur' ? blurDataURL : undefined}
      {...rest}
      onError={event => {
        setFailed(true);
        if (onError) onError(event);
      }}
    />
  );
}
